import {useMutation, useQueryClient} from '@tanstack/react-query';
import {kratosAuth} from '../api';
import {setSessionItem, removeSessionItem} from '../../../shared';
import {FlowData} from '../model';
import {useState} from 'react';

// Тип для сообщения об ошибке
interface KratosMessage {
  id: number;
  text: string;
  type: 'error' | 'info' | 'success';
}

// Тип для UI-объекта ошибки от Kratos
interface KratosUiError {
  messages?: KratosMessage[];
  nodes?: any[]; // Если нужны конкретные поля в nodes, можно уточнить
}

// Тип для ответа с ошибкой от Kratos
interface KratosErrorResponse {
  ui: KratosUiError;
}

// Тип для ошибки от axios
interface KratosAxiosError extends Error {
  response?: {
    data: KratosErrorResponse;
    status: number;
    headers: any;
  };
}

export const useAuth = () => {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Инициализация потока авторизации
  const initLoginFlowMutation = useMutation({
    mutationFn: () => kratosAuth.initLoginFlow(),
    onSuccess: (response) => {
      if (response?.data?.id) {
        const flowData: FlowData = {
          flowId: response.data.id,
          csrfToken:
            response.data.ui.nodes.find((node) => node.attributes.name === 'csrf_token')?.attributes.value || '',
        };

        setSessionItem('auth_flow_id', flowData.flowId);
        setSessionItem('auth_csrf_token', flowData.csrfToken);
      }
    },
    onError: (error: KratosAxiosError) => {
      console.error(error.response?.data);
    },
  });

  // Логин по паролю
  const loginWithPasswordMutation = useMutation({
    mutationFn: ({
      identifier,
      password,
      flowId,
      csrfToken,
    }: {
      identifier: string;
      password: string;
      flowId: string;
      csrfToken: string;
    }) => {
      return kratosAuth.submitLogin(flowId, identifier, password, csrfToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['session']});
      removeSessionItem('auth_flow_id');
      removeSessionItem('auth_csrf_token');
      removeSessionItem('auth_identifier');
      setErrorMessage(null);
    },
    onError: (error: KratosAxiosError) => {
      const messages = error?.response?.data?.ui?.messages;
      if (messages && messages.length > 0) {
        const errorMessage = messages[0];
        if (errorMessage.id === 4000006) {
          setErrorMessage('Неверный пароль');
        } else {
          setErrorMessage(errorMessage.text || 'Неизвестная ошибка');
        }
      } else {
        setErrorMessage('Произошла ошибка. Попробуйте позже.');
        console.error('Ошибка не определена:', error.message);
      }
    },
  });

  // Запрос кода для входа
  const requestCodeLoginMutation = useMutation({
    mutationFn: ({identifier, flowId, csrfToken}: {identifier: string; flowId: string; csrfToken: string}) => {
      return kratosAuth.requestCodeLogin(flowId, identifier, csrfToken);
    },
    onError: (error: KratosAxiosError) => {
      const messages = error?.response?.data?.ui?.messages;
      if (messages && messages.length > 0) {
        const errorMessage = messages[0];
        setErrorMessage(errorMessage.text || 'Неизвестная ошибка');
      } else {
        setErrorMessage('Произошла ошибка при запросе кода. Попробуйте позже.');
        console.error('Ошибка не определена:', error.message);
      }
    },
  });

  // Логин по коду
  const loginWithCodeMutation = useMutation({
    mutationFn: ({
      identifier,
      code,
      flowId,
      csrfToken,
    }: {
      identifier: string;
      code: string;
      flowId: string;
      csrfToken: string;
    }) => {
      return kratosAuth.submitCodeLogin(flowId, identifier, code, csrfToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['session']});
      removeSessionItem('auth_flow_id');
      removeSessionItem('auth_csrf_token');
      removeSessionItem('auth_identifier');
      setErrorMessage(null);
    },
    onError: (error: KratosAxiosError) => {
      const messages = error?.response?.data?.ui?.messages;
      if (messages && messages.length > 0) {
        const errorMessage = messages[0];
        if (errorMessage.id === 4000035) {
          setErrorMessage('Неправильный код');
        } else {
          setErrorMessage(errorMessage.text || 'Неизвестная ошибка');
        }
      } else {
        setErrorMessage('Произошла ошибка. Попробуйте позже.');
        console.error('Ошибка не определена:', error.message);
      }
    },
  });

  // Логаут
  const logoutMutation = useMutation({
    mutationFn: () => kratosAuth.logout(),
    onSuccess: () => {
      queryClient.setQueryData(['session'], null);
      removeSessionItem('auth_flow_id');
      removeSessionItem('auth_csrf_token');
      removeSessionItem('auth_identifier');
    },
  });

  return {
    initLoginFlow: initLoginFlowMutation.mutateAsync,
    loginWithPassword: loginWithPasswordMutation.mutateAsync,
    requestCodeLogin: requestCodeLoginMutation.mutateAsync,
    loginWithCode: loginWithCodeMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    loginPasswordError: loginWithPasswordMutation.error,
    loginCodeError: loginWithCodeMutation.error,
    isInitFlowLoading: initLoginFlowMutation.isPending,
    errorMessage,
  };
};
